import { useState, useEffect } from 'react'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DatePicker from 'react-datepicker'
import { useWindowDimensions } from '../hooks/useWindowDimensions'
import { isPhone } from '../common/constants'
import 'react-datepicker/dist/react-datepicker.css'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

interface Props {
  updateDay: (start: number, end: number, isToday: boolean) => void
}

export function ChartsPageTimeController({ updateDay }: Props) {
  const { width, height } = useWindowDimensions()
  const [periodInDays, setPeriodInDays] = useState(28)
  const [startTime, setStartTime] = useState<number>(new Date().setHours(23, 59, 59, 999) - periodInDays * ONE_DAY_MS)
  const [endTime, setEndTime] = useState<number>(new Date().setHours(23, 59, 59, 999))
  const [isLastDay, setIsLastDay] = useState(false)

  const isMobilePortraitMode = isPhone && width < height

  useEffect(() => {
    const tempEndDate = new Date(endTime)
    const isLastDay = tempEndDate.setHours(12, 0, 0, 0) >= new Date().setHours(12, 0, 0, 0)

    updateDay(startTime, endTime, isLastDay)
    setIsLastDay(isLastDay)
    const days = Math.round((endTime - startTime) / ONE_DAY_MS)
    setPeriodInDays(days)
  }, [startTime, endTime]) // eslint-disable-line react-hooks/exhaustive-deps

  function previousDay() {
    const delta = Math.round((endTime - startTime) / ONE_DAY_MS) * ONE_DAY_MS
    setStartTime(startTime - delta)
    setEndTime(endTime - delta)
  }

  function nextDay() {
    if (!isLastDay) {
      const delta = Math.round((endTime - startTime) / ONE_DAY_MS) * ONE_DAY_MS
      setStartTime(startTime + delta)
      setEndTime(endTime + delta)
    }
  }

  function setPeriod(days: number) {
    const defaultEndDateTimeInMs = new Date().setHours(23, 59, 59, 999)
    const defaultStartDateTimeInMs = defaultEndDateTimeInMs - (days === 1 ? 0 : days) * ONE_DAY_MS
    const updatedStartDate = new Date(defaultStartDateTimeInMs).setHours(0, 0, 0, 0)
    const updatedEndDate = new Date(defaultEndDateTimeInMs).setHours(23, 59, 59, 999)

    setStartTime(updatedStartDate)
    setEndTime(updatedEndDate)
  }

  return (
    <div className={isMobilePortraitMode ? 'flex flex-col' : 'flex flex-row'}>
      <div className="flex flex-row">
        <button
          className={periodInDays === 1 ? tailwind.buttonEnabled : tailwind.buttonDisabled}
          type="button"
          onClick={() => setPeriod(1)}
          disabled={periodInDays === 1}
        >
          1 day
        </button>

        <button
          className={periodInDays === 28 ? tailwind.buttonEnabled : tailwind.buttonDisabled}
          type="button"
          onClick={() => setPeriod(28)}
          disabled={periodInDays === 28}
        >
          28 days
        </button>
      </div>
      <div className="flex flex-row mt-1">
        <div className={`cursor-pointer ${isPhone ? 'px-2.5' : 'px-4'}`} onClick={previousDay}>
          <FontAwesomeIcon icon={faChevronLeft} size="lg" />
        </div>
        <div className="mr-1 cursor-pointer">
          <DatePicker
            maxDate={new Date(endTime)}
            className="cursor-pointer text-x text-center border-slate-gray border-2 w-[125px]"
            selected={new Date(startTime)}
            onChange={(date: Date) => {
              const newStartDate = date.setHours(0, 0, 0, 0)
              setStartTime(newStartDate)
            }}
          />
        </div>
        <div className="ml-1 cursor-pointer">
          <DatePicker
            maxDate={new Date()}
            minDate={new Date(startTime)}
            className="cursor-pointer text-x text-center border-slate-gray border-2 w-[125px]"
            selected={new Date(endTime)}
            onChange={(date: Date) => {
              const newEndDate = date.setHours(23, 59, 59, 999)
              setEndTime(newEndDate)
            }}
          />
        </div>
        <div
          className={`${isPhone ? 'px-2.5' : 'px-4'} ${isLastDay ? 'color-slate-100' : 'cursor-pointer'}`}
          onClick={nextDay}
        >
          <FontAwesomeIcon className={isLastDay ? 'text-slate-200' : ''} icon={faChevronRight} size="lg" />
        </div>
      </div>
      <div className="flex flex-row mt-1">{periodInDays}d</div>
    </div>
  )
}

const tailwind = {
  container: 'mx-10 pt-32 mb-32',

  buttonEnabled: `bg-blue-500 text-white font-bold py-1 rounded focus:outline-none focus:shadow-outline mb-1 mx-1
  ${isPhone ? 'w-[90px] px-2' : 'w-[110px] px-4'}`,
  buttonDisabled: `bg-gray-400 hover:bg-blue-700 text-white font-bold py-1 rounded focus:outline-none focus:shadow-outline mb-1 mx-1
  ${isPhone ? 'w-[90px] px-2' : 'w-[110px] px-4'}`,
}
